import React, { FC, useEffect, useState, useRef, useContext } from 'react'
import MessageSwitch from './MessageSwitch'
import { animateNewCalc, animateNewEnter } from './messagesAnimation/new'
import {
  animateNewAfterTypingCalc,
  animateNewAfterTypingEnter,
} from './messagesAnimation/newAfterTyping'
import { animateDelCalc, animateDelHide } from './messagesAnimation/delete'
import {
  animateTypingToMsgCalc,
  animateTypingToMsgEnter,
} from './messagesAnimation/typingToMsg'
import useResizeObserver from 'use-resize-observer'
import { Message } from '~/service/chatConn/types'
import { useDidUpdate } from '~/utils/hooks'
import { VariableSizeList as List } from 'react-window'
import classNames from 'classnames'
import { ChatContext } from '~/service/chatConn'

type MessagesProps = {
  messages: Message[]
  isAnimatingRef: React.MutableRefObject<boolean>
  scrollToBottom: () => void
  outputScroll: number
  height: number
  outputRef: React.MutableRefObject<any>
}

type QueuedMessage = {
  uid: string
  action: QueuedMessageAction
}

enum QueuedMessageAction {
  ADD = 'ADD',
  ADD_AFTER_TYPING = 'ADD_AFTER_TYPING',
  DELETE = 'DELETE',
  TYPING_TO_MSG = 'TYPING_TO_MSG',
}

export enum MessageAnimType {
  NONE = 'NONE',
  MOVE = 'MOVE',
  NEW_CALC = 'NEW_CALC',
  NEW_ENTER = 'NEW_ENTER',
  NEW_AFTER_TYPING_CALC = 'NEW_AFTER_TYPING_CALC',
  NEW_AFTER_TYPING_ENTER = 'NEW_AFTER_TYPING_ENTER',
  DEL_CALC = 'DEL_CALC',
  DEL_HIDE = 'DEL_HIDE',
  TYPING_TO_MSG_CALC = 'TYPING_TO_MSG_CALC',
  TYPING_TO_MSG_HIDE = 'TYPING_TO_MSG_HIDE',
  TYPING_TO_MSG_ENTER = 'TYPING_TO_MSG_ENTER',
}

export type VisibleMessage = {
  uid: string
  animType: MessageAnimType
  translate?: [number, number]
  bottomShift?: number
  scrollShift?: number
  animPayload?: any
}

const getToAnimate = (
  queue: QueuedMessage[],
  getMessage: (uid: string) => Message | undefined,
  messages: VisibleMessage[]
): [QueuedMessage, QueuedMessage[]] => {
  let [toAnimate, ...newQueue] = queue
  // console.log('queue wyjmowany', toAnimate, newQueue);

  if (toAnimate.action == QueuedMessageAction.ADD) {
    const prevMsg = messages.length
      ? getMessage(messages[messages.length - 1].uid)
      : undefined

    if (!prevMsg || prevMsg.type != 'TYPING') {
      return [toAnimate, newQueue]
    }

    return [
      {
        ...toAnimate,
        action: QueuedMessageAction.ADD_AFTER_TYPING,
      },
      newQueue,
    ]
  }

  if (toAnimate.action == QueuedMessageAction.DELETE) {
    const msg = getMessage(toAnimate.uid)

    if (!msg || !newQueue.length) {
      return [toAnimate, newQueue]
    }

    const msgNext = getMessage(newQueue[0].uid)

    const [, , ...restQueue] = queue

    if (msgNext) {
      if (msg.type == 'TYPING' && msgNext.type == 'CLIENT') {
        return [
          {
            uid: msgNext.uid,
            action: QueuedMessageAction.TYPING_TO_MSG,
          },
          restQueue,
        ]
      }
    }
  }

  return [toAnimate, newQueue]
}

const Messages: FC<MessagesProps> = ({
  messages,
  scrollToBottom,
  isAnimatingRef,
  outputScroll,
  height,
  outputRef,
}) => {
  const { activeChat, scrollOffset, setScrollOffset, readMessages } =
    useContext(ChatContext)
  const [allMessages, setAllMessages] = useState<Message[]>(messages)
  const [previousMessages, setPreviousMessages] = useState<Message[]>(messages)
  const [visibleMessages, setVisibleMessages] = useState<VisibleMessage[]>(() =>
    messages.map(m => ({
      uid: m.uid,
      animType: MessageAnimType.NONE,
    }))
  )
  const [queue, setQueue] = useState<QueuedMessage[]>([])

  const scrollToBottomOffset = () => {
    !scrollOffset && scrollToBottom()
  }

  const [isAnimating, setAnimating] = useState(false)
  isAnimatingRef.current = isAnimating

  const [reactWindowList, setReactWindowList] = useState(false)
  const reactWindowListRef = useRef(reactWindowList)
  reactWindowListRef.current = reactWindowList

  const ref = useRef<HTMLDivElement>(null)
  const { width = 0 } = useResizeObserver<HTMLDivElement>({ ref })

  const listRef = useRef<any>({})
  const rowHeights = useRef({})

  let maxMessages = 50

  const setRowHeight = (index, size) => {
    listRef.current.resetAfterIndex(0)
    rowHeights.current = { ...rowHeights.current, [index]: size }
  }

  const getRowHeight = index => {
    return rowHeights.current[index] || 50
  }

  const getMessage = (uid: string) => allMessages.find(m => m.uid == uid)

  // skopiowanie przybytych messages do allMessages

  const msgCompUids = messages.map(m => m.uid).join()

  // zmiana statusu wiadomości
  // potrzebne jeśli się chce odświeżyć
  // będzie można zrobić że jak konsultant wyśle wiadomość to jest na szaro
  // dopóki nie dojdzie
  // w state jest status wysyłki
  // const msgCompProps = messages
  //   .map(m => {
  //     switch (m.type) {
  //       case MessageType.USER_QUESTION:
  //         return m.state
  //     }
  //   })
  //   .join()

  useEffect(() => {
    const uids = allMessages.map(m => m.uid)

    const newMsg = messages.filter(m => !uids.includes(m.uid))
    const existingMsg = allMessages.map(oldData => {
      const actualData = messages.find(m2 => m2.uid == oldData.uid)

      return actualData ? actualData : oldData
    })

    setAllMessages([...existingMsg, ...newMsg])
  }, [msgCompUids /*, msgCompProps */])
  /**
   * messages jeszcze się zmieniają na
   * state w USER_QUESTION ANSWER_FORM USER_UPLOAD
   * canceled w ANSWER_FORM
   * read w ?
   */

  // dorzucenie nowego do kolejki

  useDidUpdate(() => {
    const allUids = messages.map(m => m.uid)
    const prevUids = previousMessages.map(m => m.uid)
    const queueUids = queue.map(m => m.uid)

    const newMessages = messages
      .filter(m => !prevUids.includes(m.uid))
      .filter(m => !queueUids.includes(m.uid))
      .map(m => ({ uid: m.uid, action: QueuedMessageAction.ADD }))

    const removedMessages = previousMessages
      .filter(m => !allUids.includes(m.uid))
      .map(m => ({ uid: m.uid, action: QueuedMessageAction.DELETE }))

    // console.log('QUEUE', newMessages, removedMessages);

    // if (removedMessages.length || newMessages.length) {
    //   console.log('-------', removedMessages, newMessages);
    // }

    setQueue(queue => [...queue, ...removedMessages, ...newMessages])
    setPreviousMessages(messages)
  }, [msgCompUids])

  // wyjęcie elementu z kolejki do animowania

  useEffect(() => {
    if (isAnimating || !queue.length) {
      return
    }

    setAnimating(true)

    setQueue(queue => {
      const [toAnimate, newQueue] = getToAnimate(
        queue,
        getMessage,
        visibleMessages
      )
      // console.log('queue wyjęty', toAnimate, newQueue)

      setVisibleMessages(
        (() => {
          switch (toAnimate.action) {
            case QueuedMessageAction.ADD:
              return [
                ...visibleMessages,
                {
                  uid: toAnimate.uid,
                  animType: MessageAnimType.NEW_CALC,
                },
              ]

            case QueuedMessageAction.ADD_AFTER_TYPING:
              return [
                ...visibleMessages,
                {
                  uid: toAnimate.uid,
                  animType: MessageAnimType.NEW_AFTER_TYPING_CALC,
                },
              ]

            case QueuedMessageAction.DELETE:
              return visibleMessages.map(m =>
                m.uid == toAnimate.uid
                  ? {
                      ...m,
                      animType: MessageAnimType.DEL_CALC,
                    }
                  : m
              )

            case QueuedMessageAction.TYPING_TO_MSG:
              return [
                ...visibleMessages,
                {
                  uid: toAnimate.uid,
                  animType: MessageAnimType.TYPING_TO_MSG_CALC,
                },
              ]
          }
        })()
      )

      return newQueue
    })
  }, [isAnimating, queue.length])

  //

  useEffect(() => {
    animateNewCalc(visibleMessages, setVisibleMessages, ref)
  }, [
    visibleMessages.filter(m => m.animType == MessageAnimType.NEW_CALC).length,
  ])

  useEffect(() => {
    animateNewEnter(
      visibleMessages,
      setVisibleMessages,
      setAnimating,
      ref,
      scrollToBottomOffset
    )
  }, [
    visibleMessages.filter(m => m.animType == MessageAnimType.NEW_ENTER).length,
  ])

  //

  useEffect(() => {
    animateNewAfterTypingCalc(visibleMessages, setVisibleMessages, ref)
  }, [
    visibleMessages.filter(
      m => m.animType == MessageAnimType.NEW_AFTER_TYPING_CALC
    ).length,
  ])

  useEffect(() => {
    animateNewAfterTypingEnter(
      visibleMessages,
      setVisibleMessages,
      setAnimating,
      ref,
      scrollToBottomOffset
    )
  }, [
    visibleMessages.filter(
      m => m.animType == MessageAnimType.NEW_AFTER_TYPING_ENTER
    ).length,
  ])

  //

  useEffect(() => {
    animateDelCalc(visibleMessages, setVisibleMessages, ref)
  }, [
    visibleMessages.filter(m => m.animType == MessageAnimType.DEL_CALC).length,
  ])

  useEffect(() => {
    animateDelHide(
      visibleMessages,
      setVisibleMessages,
      setAnimating,
      ref,
      scrollToBottomOffset
    )
  }, [
    visibleMessages.filter(m => m.animType == MessageAnimType.DEL_HIDE).length,
  ])

  //

  useEffect(() => {
    animateTypingToMsgCalc(visibleMessages, setVisibleMessages, ref)
  }, [
    visibleMessages.filter(
      m => m.animType == MessageAnimType.TYPING_TO_MSG_CALC
    ).length,
  ])

  useEffect(() => {
    animateTypingToMsgEnter(
      visibleMessages,
      setVisibleMessages,
      setAnimating,
      ref,
      scrollToBottomOffset
    )
  }, [
    visibleMessages.filter(
      m => m.animType == MessageAnimType.TYPING_TO_MSG_ENTER
    ).length,
  ])

  //

  const Row = ({ data: { messages }, index, style }) => {
    const rowRef: any = useRef({})

    useEffect(() => {
      if (rowRef.current) {
        setRowHeight(index, rowRef.current.clientHeight)
      }
    }, [rowRef])

    const message = getMessage(messages[index].uid)

    if (!message) {
      return null
    }

    return (
      <div style={style} className="list-row" id={messages[index].uid}>
        <div className="chat-output-row" ref={rowRef}>
          <MessageSwitch
            key={messages[index].uid}
            style={{}}
            message={message}
            animationPayload={{}}
            animationClassName={''}
            isLast={false}
            parentWidth={width}
          />
        </div>
      </div>
    )
  }

  const scrollList = (scrollOffset: number) => {
    const firstVisibleRow = Array.from(
      document.querySelectorAll('.list-row')
    ).find(
      (el: HTMLDivElement) =>
        parseInt(el.style.top) >=
        scrollOffset + height - parseInt(el.style.height) - 45
    )

    if (firstVisibleRow) {
      if (
        firstVisibleRow.id == visibleMessages[visibleMessages.length - 1].uid
      ) {
        setReactWindowList(false)
        scrollToBottom()
      }
    }
  }

  useEffect(() => {
    if (outputRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = outputRef.current
      const isNearBottom = scrollTop + clientHeight + 200 <= scrollHeight

      if (isNearBottom) {
        setScrollOffset(true)
      } else {
        setScrollOffset(false)
        readMessages()
      }
    }

    if (outputScroll == 0 && !reactWindowListRef.current) {
      setReactWindowList(true)
      setTimeout(() => {
        listRef.current?.scrollToItem(
          visibleMessages.length - maxMessages - 1,
          'start'
        )
      })
    }
  }, [outputScroll])

  return (
    <>
      {((scrollOffset && !reactWindowList) ||
        (reactWindowList && !activeChat.stopped)) && (
        <div
          className="chat-output-button"
          onClick={() => {
            setReactWindowList(false)
            scrollToBottom()
            readMessages()
          }}
        >
          <span className="fa-solid fa-angle-down"></span>
          {activeChat.unread > 0 ? (
            <div className="button-badge">{activeChat.unread}</div>
          ) : null}
        </div>
      )}
      {reactWindowList && visibleMessages.length > maxMessages && (
        <List
          className="chat-output-list"
          itemCount={visibleMessages.length}
          itemSize={getRowHeight}
          height={height}
          ref={listRef}
          itemData={{ messages: visibleMessages }}
          onScroll={e => {
            scrollList(e.scrollOffset)
          }}
        >
          {Row}
        </List>
      )}
      <div
        ref={ref}
        className={classNames('chat-output-content', {
          'chat-output-content--hidden':
            reactWindowList && visibleMessages.length > maxMessages,
        })}
      >
        {visibleMessages.map((vMsg, k) => {
          let style = {}
          let animationClassName = ''

          switch (vMsg.animType) {
            case MessageAnimType.NEW_CALC:
              animationClassName = 'is-animate-new-calc'
              break

            case MessageAnimType.NEW_ENTER:
              style = { bottom: `${vMsg.bottomShift}px` }
              animationClassName = 'is-animate-new-enter'
              break

            case MessageAnimType.NEW_AFTER_TYPING_CALC:
              animationClassName = 'is-animate-new-calc'
              break

            case MessageAnimType.NEW_AFTER_TYPING_ENTER:
              style = { bottom: `${vMsg.bottomShift}px` }
              animationClassName = 'is-animate-new-enter'
              break

            case MessageAnimType.DEL_HIDE:
              animationClassName = 'is-animate-del-hide'
              break

            case MessageAnimType.MOVE:
              style = {
                transform: `translateY(${
                  vMsg.translate && vMsg.translate[1]
                }px)`,
              }
              animationClassName = 'is-animate-move'
              break

            case MessageAnimType.TYPING_TO_MSG_CALC:
              animationClassName = 'is-animate-typingToMsg-calc'
              break

            case MessageAnimType.TYPING_TO_MSG_ENTER:
              style = { bottom: `${vMsg.bottomShift}px` }
              animationClassName = 'is-animate-typingToMsg-enter'
              break

            case MessageAnimType.TYPING_TO_MSG_HIDE:
              animationClassName = 'is-animate-typingToMsg-hide'
              break
          }

          let message = getMessage(vMsg.uid)

          if (!message) {
            return null
          }

          if (k <= visibleMessages.length - maxMessages) {
            return null
          }

          return (
            <MessageSwitch
              key={vMsg.uid}
              style={style}
              message={message}
              animationPayload={vMsg.animPayload || {}}
              animationClassName={animationClassName}
              isLast={k == visibleMessages.length - 1}
              parentWidth={width}
            />
          )
        })}
      </div>
    </>
  )
}

export default Messages
